<template>
  <ScreenWidthProvider
    ref="testimonials"
    v-slot="{ isWider }"
    class="tab-pane product-content__testimonials pt-6 pb-12"
    role="tabpanel"
  >
    <div
      v-if="loading && !initialized"
      :class="{ 'columns-2': isWider('md') }"
    >
      <div
        v-for="i in 6"
        :key="i"
        class="product__testimonials__item"
      >
        <Stars
          :score="0"
          classes="product__testimonials__stars"
          size="xl"
        />
        <b-skeleton width="50%" />
        <b-skeleton height="3rem" />
      </div>
    </div>

    <InfiniteScroll
      v-else
      class="product__testimonials"
      :scolling-offset="200"
      :interval="60"
      :disabled="showTestimonials >= reviews.length"
      @fetch="showMoreTestimonials"
    >
      <template #content>
        <SameHeightColumns :columns="columnBreakpoints">
          <div
            v-if="reviewsAverage && reviewsAverage.avg"
            ref="reviewSummary"
            :data-i18n="reviewsAverage.avgI18n"
            class="product-reviews__blocks bg-blue-750 text-white rounded-2xl px-6 py-4 mb-8 flex flex-col"
          >
            <div class="flex flex-col justify-center items-center grow w-full text-center">
              <div class="product-reviews__block_review_rating flex justify-center items-end mb-2">
                <h4
                  class="mb-0"
                  :data-i18n="reviewsAverage.avgI18n"
                >
                  {{ reviewsAverageText }}
                </h4>
                <span class="mb-2 md:mb-1 font-secondary">
                  /5
                </span>
              </div>
              <Stars
                class="mb-4"
                size="xl"
                :data-i18n="reviewsAverage.avgI18n"
                :score="reviewsAverage.avg"
              />
              <h5
                class="product-reviews__block_review_counts px-6 mb-6 font-normal font-secondary"
                :data-i18n="reviewsAverage.countI18n"
                v-html="formatReviewCount"
              />
            </div>
            <ProgressBarGrid
              class="w-full"
              :rows="testimonialStatistics"
            />
          </div>
          <MachineTranslatedReviewsNotice class="py-4 text-center" />
          <Testimonial
            v-for="(review, index) in testimonials"
            :key="index"
            class="mb-4 pointer-events-none"
            v-bind="review"
          />
        </SameHeightColumns>
      </template>
    </InfiniteScroll>
  </ScreenWidthProvider>
</template>

<script>
import scrollMixin from '@packages/scroll/mixins/scroll.mixin.js'

export default defineNuxtComponent({
  mixins: [scrollMixin],

  props: {
    reviewsAverage: {
      type: Object,
      required: true,
    },

    reviews: {
      type: Array,
      required: true,
    },

    ratingsChart: {
      type: Object,
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      showTestimonials: 20,
      extraTestimonialsPerScroll: 20,
      initialized: false,
    }
  },

  computed: {
    columnBreakpoints () {
      return { 'xs': 1, 'md': 2 }
    },

    testimonials () {
      return this.reviews.slice(0, this.showTestimonials)
    },

    formatReviewCount () {
      const { count } = this.reviewsAverage
      return this.$t('reviewAmountPaxText', count).replace(/[0-9]+/, `<br/><strong>${count}</strong>`)
    },

    testimonialStatistics () {
      const percentWidthOfRatingBar = score => (this.ratingsChart[score] * 100) / this.reviewsAverage.count

      const rows = []
      for (let score = 5; score > 0; score--) {
        rows.push({
          text: score,
          percentage: percentWidthOfRatingBar(score),
          value: this.ratingsChart[score],
        })
      }

      return rows
    },

    reviewsAverageText () {
      return formatNumber(this.reviewsAverage?.avg)?.slice(0, 3)
    },
  },

  mounted () {
    if (this.$route.query.top !== undefined) {
      this.$nextTick(() => this.scrollTo(this.$refs.testimonials.$el))
    }

    window.window.setTimeout(() => (this.initialized = true), 500)
  },

  methods: {
    showMoreTestimonials () {
      this.showTestimonials += this.extraTestimonialsPerScroll
    },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/product/_product-testimonials";
@import "@layers/web/assets/scss/modules/product/_product-testimonials-summary";
</style>
